import React, { useState, useEffect } from 'react';
import { X, ExternalLink, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { projectBoardService } from '../../api/boardApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#37559e','#0f766e','#7c3aed','#b45309','#be185d','#0369a1','#15803d','#c2410c'];
const getInitials    = (name = '') => name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
const avatarColor    = (name = '') => AVATAR_PALETTE[(name.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const Label = ({ children, hint }) => (
  <div className="flex items-baseline gap-1.5 mb-1.5">
    <span className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{children}</span>
    {hint && <span className="text-[11px]" style={{ color: SYSTEM_COLORS.textMuted }}>{hint}</span>}
  </div>
);

const inputCls = 'w-full rounded-xl border px-3 py-2 text-sm outline-none transition-colors';
const inputStyle = {
  borderColor: SYSTEM_COLORS.borderSoft,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};

// ── TaskModal ─────────────────────────────────────────────────────────────────

const TaskModal = ({ isOpen, onClose, onSave, task, projectId, teamMembers = [], hideReviewSection = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    assignee: {},
    status: 'todo',
    evidence: [],
    isDisposed: false,
    review: { status: 'pending', comments: [] },
  });

  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (task) {
      const validAssignees = Object.entries(task.assignee || {})
        .filter(([, a]) => a?.name)
        .reduce((acc, [id, a]) => { acc[id] = a; return acc; }, {});
      setFormData({
        title:       task.title       || '',
        description: task.description || '',
        startDate:   task.startDate   || new Date().toISOString().split('T')[0],
        dueDate:     task.dueDate     || '',
        assignee:    validAssignees,
        status:      task.status      || 'todo',
        evidence:    Array.isArray(task.evidence) ? task.evidence : [],
        isDisposed:  task.isDisposed  || false,
        review:      task.review      || { status: 'pending', comments: [] },
      });
    } else {
      setFormData({
        title: '', description: '',
        startDate: new Date().toISOString().split('T')[0],
        dueDate: '', assignee: {}, status: 'todo',
        evidence: [], isDisposed: false,
        review: { status: 'pending', comments: [] },
      });
    }
  }, [task, isOpen]);

  useEffect(() => {
    if (!projectId || !isOpen) return;
    setDocsLoading(true);
    projectBoardService.getProjectDocuments(projectId)
      .then(setDocuments)
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setDocsLoading(false));
  }, [projectId, isOpen]);

  const toggleAssignee = (member) => {
    setFormData(prev => {
      const next = { ...prev.assignee };
      if (next[member.id]) delete next[member.id];
      else next[member.id] = { id: member.id, name: member.name, email: member.email, role: member.role, skills: member.skills || [] };
      return { ...prev, assignee: next };
    });
  };

  const toggleEvidence = (id) => {
    setFormData(prev => ({
      ...prev,
      evidence: prev.evidence.includes(id)
        ? prev.evidence.filter(e => e !== id)
        : [...prev.evidence, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error('Title is required'); return; }
    if (Object.keys(formData.assignee).length === 0) { toast.error('Assign at least one team member'); return; }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch {
      toast.error('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const assignedIds = new Set(Object.keys(formData.assignee));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="relative w-full sm:max-w-lg flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden border"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowStrong, maxHeight: '92dvh' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <div>
            <h3 className="text-base font-bold text-white">
              {task ? 'Edit Task' : 'New Task'}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.72)' }}>
              {task ? 'Update task details below' : 'Fill in the details to create a task'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-lg transition-colors"
            style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.85)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <X size={17} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Title */}
          <div>
            <Label>Title</Label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              placeholder="What needs to be done?"
              className={inputCls}
              style={inputStyle}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              placeholder="Describe the task…"
              rows={3}
              className={inputCls}
              style={{ ...inputStyle, resize: 'none' }}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <input
                type="date"
                value={formData.startDate}
                onChange={e => setFormData(p => ({ ...p, startDate: e.target.value }))}
                className={inputCls}
                style={inputStyle}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData(p => ({ ...p, dueDate: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                required
              />
            </div>
          </div>

          {/* Assignees */}
          <div>
            <Label hint="(select one or more)">Assignees</Label>
            <div
              className="flex flex-wrap gap-2 rounded-xl border p-2.5"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
            >
              {teamMembers.length === 0 && (
                <p className="text-xs w-full text-center py-1" style={{ color: SYSTEM_COLORS.textMuted }}>No team members available</p>
              )}
              {teamMembers.map(m => {
                const active = assignedIds.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleAssignee(m)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: active ? SYSTEM_COLORS.primaryTint : '#f8fafc',
                      color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                      border: `1px solid ${active ? BRAND_COLORS.primary + '55' : SYSTEM_COLORS.borderSoft}`,
                    }}
                  >
                    <span
                      className="flex items-center justify-center rounded-full text-white font-bold"
                      style={{ width: 18, height: 18, fontSize: 9, backgroundColor: active ? BRAND_COLORS.primary : avatarColor(m.name) }}
                    >
                      {getInitials(m.name)}
                    </span>
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Evidence */}
          <div>
            <Label hint="(link project docs as evidence)">Evidence of Work</Label>
            <div
              className="max-h-36 overflow-y-auto rounded-xl border divide-y"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, divideColor: SYSTEM_COLORS.borderSoft }}
            >
              {docsLoading ? (
                <div className="flex items-center justify-center gap-2 py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-2" style={{ borderTopColor: BRAND_COLORS.primary, borderColor: SYSTEM_COLORS.borderSoft }} />
                  <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>Loading documents…</span>
                </div>
              ) : documents.length === 0 ? (
                <p className="text-xs text-center py-4" style={{ color: SYSTEM_COLORS.textMuted }}>No project documents yet</p>
              ) : (
                documents.map(doc => {
                  const checked = formData.evidence.includes(doc.id);
                  return (
                    <div
                      key={doc.id}
                      onClick={() => toggleEvidence(doc.id)}
                      className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors"
                      style={{ backgroundColor: checked ? SYSTEM_COLORS.primaryTint : 'transparent' }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                      onMouseLeave={e => { if (!checked) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div
                        className="flex items-center justify-center rounded flex-shrink-0"
                        style={{
                          width: 16, height: 16,
                          border: `2px solid ${checked ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft}`,
                          backgroundColor: checked ? BRAND_COLORS.primary : 'transparent',
                        }}
                      >
                        {checked && <CheckCircle2 size={10} color="#fff" strokeWidth={3} />}
                      </div>
                      {doc.type === 'link'
                        ? <ExternalLink size={13} style={{ color: SYSTEM_COLORS.textMuted, flexShrink: 0 }} />
                        : <FileText size={13} style={{ color: SYSTEM_COLORS.textMuted, flexShrink: 0 }} />
                      }
                      <span className="text-xs truncate flex-1" style={{ color: checked ? BRAND_COLORS.primary : NEUTRAL_COLORS.black }}>
                        {doc.name}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2.5 px-5 py-4 flex-shrink-0"
          style={{ borderTop: `1px solid ${SYSTEM_COLORS.borderSoft}`, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            style={{ backgroundColor: SYSTEM_COLORS.borderSoft, color: NEUTRAL_COLORS.black }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            {saving && <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/40 border-t-white" />}
            {saving ? 'Saving…' : task ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
